// src/components/Checklist.js

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';


import {
    fetchChecklists,
    addChecklistItem,
    deleteChecklistItem,
    deleteChecklist,
    localDeleteChecklist,
    localAddItem,
    localUpdateItem,
    localDeleteItem,
    localNewChecklist,
    setVisibleAddNew,
    localReorderItems
} from '../../redux/Slices/checklistSlice';

import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';

// Pragmatic DND imports
import {
    draggable,
    dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";


import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
    attachClosestEdge,
    extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";

import './scss/Checklists.scss';
import Checkbox from '../Global/Checkbox';
import { config } from '../../config';
import Spinner from '../Global/Spinner';
import { marked } from 'marked';
import Checklist from '../icons/Checklist';
import Delete from '../icons/Delete';
import MoreHoriz from '../icons/MoreHoriz';
import DragIndicator from '../icons/DragIndicator';
import Close from '../icons/Close';

// Draggable Checklist Item Component
// Updated DraggableChecklistItem component with fixed drop zones

const DraggableChecklistItem = ({
    item,
    checklistId,
    index,
    editingItemId,
    editItemTitle,
    isEditing,
    isTextareaFocused,
    handleCheck,
    handleItemMore,
    setEditingItemId,
    setEditItemTitle,
    setIsEditing,
    setIsTextareaFocused,
    updateItemTitle,
    checklistItemRef,
    onReorder,
    isReadOnly = false
}) => {
    const itemRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isDropTarget, setIsDropTarget] = useState(false);
    const [closestEdge, setClosestEdge] = useState(null);

    useEffect(() => {
        const element = itemRef.current;
        if (!element) return;

        // Skip drag-and-drop setup if read-only
        if (isReadOnly) return;

        const combined = combine(
            // Draggable configuration
            draggable({
                element,
                canDrag: ({ input }) => {
                    // Prevent drag if clicking on interactive elements
                    const target = input?.event?.target;
                    if (!target) return true;
                    const isInteractiveElement = target.closest('input, button, textarea, a, [data-popover-trigger]');
                    return !isInteractiveElement;
                },
                getInitialData: () => ({
                    type: 'CHECKLIST_ITEM',
                    itemId: item._id,
                    checklistId,
                    index
                }),
                onDragStart: () => setIsDragging(true),
                onDrop: () => {
                    setIsDragging(false);
                    setIsDropTarget(false);
                    setClosestEdge(null);
                },
            }),

            // Drop target configuration
            dropTargetForElements({
                element,
                getData: ({ input, element }) => {
                    const data = {
                        type: 'CHECKLIST_ITEM',
                        itemId: item._id,
                        checklistId,
                        index
                    };
                    return attachClosestEdge(data, {
                        input,
                        element,
                        allowedEdges: ["top", "bottom"],
                        threshold: 0.5
                    });
                },
                canDrop: ({ source }) => {
                    return source.data.type === 'CHECKLIST_ITEM' &&
                        source.data.checklistId === checklistId;
                },
                onDrag: (args) => {
                    const { source, self } = args;

                    if (!self || !source) return;

                    // Skip if dragging the same item
                    if (self.data.itemId === source.data.itemId) {
                        setIsDragging(true);
                        return;
                    }

                    const edge = extractClosestEdge(self.data);
                    setClosestEdge(edge);
                    setIsDropTarget(true);
                },
                onDragLeave: () => {
                    setIsDropTarget(false);
                    setClosestEdge(null);
                },
                onDrop: (args) => {
                    const { source, self } = args;

                    if (
                        source.data.type !== 'CHECKLIST_ITEM' ||
                        source.data.checklistId !== checklistId
                    ) {
                        return;
                    }

                    const sourceIndex = source.data.index;
                    const targetIndex = self.data.index;
                    const edge = extractClosestEdge(self.data);

                    let destinationIndex = targetIndex;

                    if (edge === 'bottom') {
                        destinationIndex = targetIndex + 1;
                    }

                    // Correct shift: if dragging from below, and inserting before — shift destination back
                    if (sourceIndex < destinationIndex) {
                        destinationIndex -= 1;
                    }

                    if (sourceIndex !== destinationIndex) {
                        onReorder(checklistId, sourceIndex, destinationIndex);
                    }

                    setIsDropTarget(false);
                    setClosestEdge(null);
                }
            })
        );

        return combined;
    }, [item._id, checklistId, index, onReorder]);

    return (
        <div
            ref={itemRef}
            className={`checklist-item-wrapper ${isDragging ? 'dragging' : ''} ${isDropTarget ? 'drop-target' : ''}`}
            style={{ position: 'relative' }}
        >
            {/* Drop indicator - consistent positioning like workspace sidebar */}
            {isDropTarget && closestEdge && (
                <div
                    style={{
                        position: 'absolute',
                        left: '30px',
                        right: 0,
                        height: '2px',
                        width: 'calc(100% - 30px)',
                        backgroundColor: 'var(--brand-color)',
                        zIndex: 1000,
                        // Fix: Use consistent positioning for adjacent drop zones
                        top: closestEdge === 'top' ? '-1px' : 'auto',
                        bottom: closestEdge === 'bottom' ? '-1px' : 'auto',
                        borderRadius: '50px',
                        transition: 'all 0.2s ease',
                    }}
                />
            )}

            {!isReadOnly && (
                <div className="drag-indicator">
                    <DragIndicator size={20} />
                </div>
            )}
            <div className={`checklist-item ${(isEditing && editingItemId === item._id) ? 'currently-editing' : ''} ${item.checked ? 'checked' : ''}`}>
                <Checkbox
                    checked={item.checked}
                    id={`item-${item._id}`}
                    onChange={() => !isReadOnly && handleCheck(checklistId, item._id, item.title)}
                    disabled={isReadOnly}
                />

                {editingItemId !== item._id ? (
                    <div className='checklist-label'>
                        <label
                            onClick={(e) => {
                                if (isReadOnly) return;
                                const target = e.target;
                                if (target.tagName.toLowerCase() === 'a') {
                                    e.preventDefault();
                                    window.open(target.href, '_blank');
                                    return;
                                }
                                setEditingItemId(item._id);
                                setEditItemTitle(item.title);
                                setIsEditing(true);
                            }}
                            style={{ cursor: isReadOnly ? 'default' : 'pointer' }}
                            dangerouslySetInnerHTML={{ __html: marked(item.title || '') }}
                        ></label>

                        {!isReadOnly && (
                            <span
                                id={`popover-checklist-item-more-${item._id}`}
                                onClick={(e) => handleItemMore(e, checklistId, item)}
                                data-popover-trigger
                            >
                                <MoreHoriz size={20} />
                            </span>
                        )}
                    </div>
                ) : (
                    <div className="checklist-footer-container">
                        <div className="add-item">
                            <div className={`textarea-wrapper ${isTextareaFocused ? 'focused' : ''}`}>
                                <textarea
                                    type="text"
                                    ref={checklistItemRef}
                                    placeholder="Add an item"
                                    value={editItemTitle}
                                    onChange={(e) => setEditItemTitle(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            updateItemTitle(checklistId, item._id, editItemTitle, item.checked);
                                        }
                                    }}
                                    rows={1}
                                    style={{ overflow: 'hidden' }}
                                    onFocus={() => setIsTextareaFocused(true)}
                                    onBlur={() => setIsTextareaFocused(false)}
                                />
                            </div>
                        </div>
                        <div className="checklist-footer">
                            <div className='left-button'>
                                <button className="add-btn" onClick={() => updateItemTitle(checklistId, item._id, editItemTitle, item.checked)}>Save</button>
                                <button className="cancel-btn" onClick={() => {
                                    setEditingItemId(null);
                                    setEditItemTitle('');
                                    setIsEditing(false);
                                }}>
                                    <Close size={18} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const Checklists = ({ cardId, isReadOnly = false }) => {
    const dispatch = useDispatch();
    const [newItem, setNewItem] = useState('');
    const [editingItemId, setEditingItemId] = useState(null);
    const [editItemTitle, setEditItemTitle] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [hideCheckedItems, setHideCheckedItems] = useState(false); // Track whether to hide checked items
    const [isTextareaFocused, setIsTextareaFocused] = useState(false); // New focus state

    const [editingChecklistId, setEditingChecklistId] = useState(null);
    const [editChecklistTitle, setEditChecklistTitle] = useState('');
    const [localChecklistTitles, setLocalChecklistTitles] = useState({});
    const [isTitleInputFocused, setIsTitleInputFocused] = useState(false);




    const { handlePopoverClick } = useHandlePopoverClick();

    const checklistItemRef = useRef(null);


    const { checklists, visibleAddNew, status } = useSelector((state) => state.checklists);



    useEffect(() => {
        dispatch(fetchChecklists(cardId));
    }, [cardId]);

    const updateChecklistItem = async ({ cardId, checklistId, itemId, title, checked }) => {
        try {

            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/checklists/${checklistId}/items/${itemId}`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',

                },
                credentials: 'include',
                body: JSON.stringify({ title, checked })
            });

            if (!response.ok) {
                throw new Error(`Error: ${response.statusText}`);
            }

            const data = await response.json();
            return data.checklists;
        } catch (error) {
            console.error('Error updating checklist item:', error.message);
            throw error;
        }
    };

    const updateChecklistTitle = async ({ cardId, checklistId, title }) => {
        try {


            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/checklists/${checklistId}`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ title })
            });



            if (!response.ok) throw new Error(`Error: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            console.error('Failed to update checklist title:', error.message);
            throw error;
        }
    };

    // Function to reorder checklist items
    const reorderChecklistItems = async ({ cardId, checklistId, items }) => {
        try {
            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/checklists/${checklistId}/reorder`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ items })
            });

            if (!response.ok) throw new Error(`Error: ${response.statusText}`);
            const data = await response.json();

            // Update with server response to ensure consistency
            if (data.checklists) {
                dispatch({
                    type: 'checklists/localUpdate',
                    payload: data.checklists
                });
            }


            return data;
        } catch (error) {
            console.error('Failed to reorder checklist items:', error.message);
            // Revert optimistic update on error
            dispatch(fetchChecklists(cardId));
            throw error;
        }
    };

    // Function to handle checklist item reordering
    const handleItemReorder = (checklistId, sourceIndex, targetIndex) => {
        const checklist = checklists.find(cl => cl._id === checklistId);
        if (!checklist) return;

        // Get all items (including filtered ones for proper indexing)
        const allItems = [...checklist.items];
        const visibleItems = allItems.filter(item => !hideCheckedItems || !item.checked);

        // Map visible indices to actual indices
        const sourceItem = visibleItems[sourceIndex];
        const targetItem = visibleItems[targetIndex];

        const actualSourceIndex = allItems.findIndex(item => item._id === sourceItem._id);
        const actualTargetIndex = allItems.findIndex(item => item._id === targetItem._id);

        // Reorder in the full items array
        const [movedItem] = allItems.splice(actualSourceIndex, 1);
        allItems.splice(actualTargetIndex, 0, movedItem);


        // ✅ Optimistic update
        dispatch(localReorderItems({
            checklistId,
            sourceIndex: actualSourceIndex,
            destinationIndex: actualTargetIndex
        }));

        // API call to persist the change
        reorderChecklistItems({
            cardId,
            checklistId,
            items: allItems.map(item => item._id)
        });
    };


    const handleCheck = (checklistId, itemId, itemTitle) => {

        // Optimistic update
        const item = checklists.find(checklist => checklist._id === checklistId).items.find(item => item._id === itemId);
        dispatch(localUpdateItem({ checklistId, itemId: itemId, title: itemTitle, checked: !item.checked }));

        // Actual API call
        updateChecklistItem({
            cardId,
            checklistId,
            itemId: itemId,
            title: itemTitle,
            checked: !item.checked
        });


    };


    const handleAddItem = (checklistId) => {
        if (newItem.trim()) {
            // Find the checklist and add the new item to the list of items
            const updatedChecklists = checklists.map(checklist => {
                if (checklist._id === checklistId) {
                    return {
                        ...checklist,
                        items: [...checklist.items, { _id: new Date().toISOString(), title: newItem, checked: false }] // Temporary item added locally
                    };
                }
                return checklist;
            });

            // Dispatch a local state update to optimistically update the UI
            dispatch({
                type: 'checklists/localUpdate',
                payload: updatedChecklists
            });

            dispatch(localAddItem({ checklistId, newItem }));


            // Dispatch the actual action to add the new item to the backend
            dispatch(addChecklistItem({ cardId, checklistId, newItem }));

            // Clear the new item input field but keep it visible for adding another item
            setNewItem('');
            dispatch(setVisibleAddNew(checklistId));  // Keep the input open for the same checklist
        }
    };



    const updateItemTitle = (checklistId, itemId, title, checked) => {

        if (title) {
            // Optimistic update
            dispatch(localUpdateItem({ checklistId, itemId, title, checked }));
            updateChecklistItem({ cardId, checklistId, itemId, title, checked });
            setIsEditing(false);
            setEditingItemId(null);
            setEditItemTitle('');
        }
    };

    const handleDeleteChecklist = (e, checklistId) => {
        handlePopoverClick(e, 'deleteAction', { type: 'CHECKLIST', id: checklistId, cardId });

    };

    const handleItemMore = (e, checklistId, item) => {
        handlePopoverClick(e, 'more', { type: 'CHECKLIST_ITEM_MORE', checklistId, item, cardId });
    }

    useEffect(() => {
        if (checklistItemRef.current) {
            checklistItemRef.current.style.height = '22px';
            checklistItemRef.current.style.height = `${checklistItemRef.current.scrollHeight}px`;
        }
    }, [editItemTitle, newItem]);

    useEffect(() => {
        if (checklistItemRef.current) {
            checklistItemRef.current.focus();
            checklistItemRef.current.select();
        }
    }, [visibleAddNew, editingItemId]);

    const progress = (items) => items?.length ? (items?.filter(item => item?.checked).length / items.length) * 100 : 0;

    if (status === 'loading') {
        return null;
    }


    return (
        checklists && (
            checklists.map(checklist => (
                <div className="zoobbe-checklist" key={checklist._id}>
                    <div className="checklist-header">

                        <h3 className="editable-checklist-title">
                            <div className='checklist-icon'>
                                <Checklist size={24} />
                            </div>
                            {editingChecklistId === checklist._id ? (
                                <input
                                    type="text"
                                    value={editChecklistTitle}
                                    autoFocus
                                    onChange={(e) => setEditChecklistTitle(e.target.value)}
                                    className={`checklist-title-input ${isTitleInputFocused ? 'focus' : ''}`}
                                    onFocus={() => setIsTitleInputFocused(true)}
                                    onBlur={() => {
                                        const trimmed = editChecklistTitle.trim();
                                        if (trimmed && trimmed !== checklist.title) {
                                            // 1. Optimistically update local title
                                            setLocalChecklistTitles(prev => ({
                                                ...prev,
                                                [checklist._id]: trimmed
                                            }));

                                            // 2. Update backend
                                            updateChecklistTitle({
                                                cardId,
                                                checklistId: checklist._id,
                                                title: trimmed
                                            });
                                        }

                                        setEditingChecklistId(null);
                                    }}


                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.target.blur(); // Triggers `onBlur` to save
                                        }
                                    }}
                                />
                            ) : (
                                <span
                                    className="checklist-title-text"
                                    onClick={() => {
                                        setEditingChecklistId(checklist._id);
                                        setEditChecklistTitle(localChecklistTitles[checklist._id] || checklist.title);
                                    }}
                                >
                                    {localChecklistTitles[checklist._id] || checklist.title}
                                </span>
                            )}
                        </h3>

                        {editingChecklistId !== checklist._id && (
                            <div className="checklist-more-buttons">
                                <button
                                    className="hide-checked-item-btn"
                                    onClick={() => setHideCheckedItems(!hideCheckedItems)}
                                >
                                    {hideCheckedItems ? (
                                        <span className="material-symbols-outlined">radio_button_partial</span>
                                    ) : (
                                        <span className="material-symbols-outlined">radio_button_unchecked</span>
                                    )}
                                    {hideCheckedItems ? 'Show all' : 'Hide checked'}
                                </button>

                                {!isReadOnly && (
                                    <button
                                        className="checklist-delete-btn"
                                        onClick={(e) => handleDeleteChecklist(e, checklist._id)}
                                        data-popover-trigger
                                    >
                                        <Delete size={18} />
                                        Delete
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="progress-bar">
                        <span>{Math.round(progress(checklist.items))}%</span>
                        <div className='checklist-progress-bar'>
                            <div className="progress" style={{ width: `${progress(checklist.items)}%` }}></div>
                        </div>
                    </div>
                    <div className="checklist-items">
                        {checklist.items.filter(item => !hideCheckedItems || !item.checked).map((item, index) => (
                            item && item._id && (
                                <DraggableChecklistItem
                                    key={item._id}
                                    item={item}
                                    checklistId={checklist._id}
                                    index={index}
                                    editingItemId={editingItemId}
                                    editItemTitle={editItemTitle}
                                    isEditing={isEditing}
                                    isTextareaFocused={isTextareaFocused}
                                    handleCheck={handleCheck}
                                    handleItemMore={handleItemMore}
                                    setEditingItemId={setEditingItemId}
                                    setEditItemTitle={setEditItemTitle}
                                    setIsEditing={setIsEditing}
                                    setIsTextareaFocused={setIsTextareaFocused}
                                    updateItemTitle={updateItemTitle}
                                    checklistItemRef={checklistItemRef}
                                    onReorder={handleItemReorder}
                                    isReadOnly={isReadOnly}
                                />
                            )
                        ))}
                        {!isReadOnly && (
                            visibleAddNew === checklist._id ? (
                                <div className="checklist-footer-container">
                                    <div className="add-item">
                                        <div className={`textarea-wrapper ${isTextareaFocused ? 'focused' : ''}`}>

                                            <textarea
                                                type="text"
                                                ref={checklistItemRef}
                                                placeholder="Add an item"
                                                value={newItem}
                                                onChange={(e) => setNewItem(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleAddItem(checklist._id);
                                                    }
                                                }}
                                                rows={1}
                                                style={{ overflow: 'hidden' }}
                                                onFocus={() => setIsTextareaFocused(true)}  // Set focus state
                                                onBlur={() => setIsTextareaFocused(false)}   // Remove focus state
                                            />
                                        </div>
                                    </div>
                                    <div className="checklist-footer">
                                        <div className='left-button'>
                                            <button className="add-btn" onClick={() => handleAddItem(checklist._id)}>Add</button>
                                            <button className="cancel-btn" onClick={() => { setNewItem(''); dispatch(setVisibleAddNew(null)); }}>Discard</button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <button className="add-new-item-btn" onClick={() => dispatch(setVisibleAddNew(checklist._id))}>Add item</button>
                            )
                        )}
                    </div>
                </div >
            ))
        )
    );
};

export default Checklists;
